<template>
  <NuxtLayout name="default">
    <section
      class="error-hero relative isolate flex min-h-screen flex-col justify-center px-4 py-20 sm:px-6 md:py-28 lg:px-8"
    >
      <div class="absolute inset-0 -z-10 overflow-visible">
        <div
          class="absolute -top-48 left-1/2 h-80 w-80 -translate-x-1/2 rounded-full bg-[#dbeafe] opacity-70 blur-3xl"
        />
        <div
          class="absolute bottom-[-6rem] right-0 h-[22rem] w-[22rem] translate-x-1/3 rounded-full bg-[#f5f9ff] opacity-80 blur-3xl"
        />
        <div class="absolute inset-0 bg-gradient-to-br from-[#f5f9ff] via-white to-[#d0e1fd]" />
      </div>

      <div
        class="relative mx-auto flex w-full max-w-4xl flex-col items-center gap-8 rounded-[2rem] border border-[#dbeafe] bg-white/95 p-8 text-center shadow-2xl backdrop-blur-xl sm:gap-10 sm:p-10 lg:p-14"
      >
        <span
          class="inline-flex items-center gap-2 rounded-full bg-[#eff6ff] px-4 py-1 text-xs font-semibold uppercase tracking-wider text-[#1d4ed8] sm:text-sm"
        >
          <span class="h-2 w-2 rounded-full bg-[#3b82f6]" />
          {{ statusLabel }}
        </span>

        <div class="flex flex-col items-center gap-4">
          <h1
            class="bg-gradient-to-r from-[#10254b] via-[#295bac] to-[#3b82f6] bg-clip-text text-5xl font-bold tracking-tight text-transparent sm:text-6xl md:text-7xl lg:text-8xl"
          >
            {{ headline }}
          </h1>
          <p class="text-lg font-semibold text-[#1e293b] sm:text-xl md:text-2xl">
            {{ title }}
          </p>
          <p class="max-w-2xl text-base text-[#475569] sm:text-lg">
            {{ description }}
          </p>
        </div>

        <div class="flex w-full flex-col gap-3 sm:flex-row sm:justify-center sm:gap-4">
          <button
            type="button"
            @click="goHome"
            class="inline-flex items-center justify-center rounded-full bg-[#3b82f6] px-6 py-3 font-semibold text-white shadow-[0_20px_40px_rgba(59,130,246,0.35)] transition hover:bg-[#326fd1] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3b82f6] focus-visible:ring-offset-2"
          >
            Take me home
          </button>
          <button
            type="button"
            @click="goBack"
            class="inline-flex items-center justify-center rounded-full border border-[#abc9fb] px-6 py-3 font-semibold text-[#1d4ed8] transition hover:border-[#609af8] hover:text-[#1e40af] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#93c5fd] focus-visible:ring-offset-2"
          >
            Go back
          </button>
        </div>

        <div class="flex flex-wrap items-center justify-center gap-2 text-sm text-[#475569]">
          <span>Looking to explore instead?</span>
          <button
            type="button"
            @click="goToExperiences"
            class="font-semibold text-[#295bac] underline-offset-4 transition hover:text-[#1d4ed8] hover:underline"
          >
            Discover experiences
          </button>
        </div>
      </div>
    </section>
  </NuxtLayout>
</template>

<script setup lang="ts">
import { computed } from 'vue';

const error = useError();
const is404 = computed(() => error.value?.statusCode === 404);

const headline = computed(() => (is404.value ? '404' : error.value?.statusCode ?? 'Oops!'));
const title = computed(() =>
  is404.value ? 'This page has checked out early.' : error.value?.statusMessage || 'Something went wrong.'
);
const description = computed(() =>
  is404.value
    ? 'The destination you are looking for seems to have wandered off. Let\'s guide you back to somewhere inspiring.'
    : 'We hit a snag while loading this page. Try going back, or head home to continue exploring Tripening.'
);
const statusLabel = computed(() => (is404.value ? 'Page not found' : 'Unexpected error'));

useHead(() => ({
  title: is404.value ? 'Page not found | Tripening' : `Error | Tripening`,
  meta: [
    { name: 'robots', content: 'noindex, nofollow' }
  ]
}));

const goHome = () => {
  clearError({ redirect: '/' });
};

const goBack = () => {
  if (process.client && window.history.length > 1) {
    clearError();
    window.history.back();
  } else {
    goHome();
  }
};

const goToExperiences = () => {
  clearError({ redirect: '/experiences' });
};
</script>

<style scoped>
.error-hero {
  width: 100vw;
  margin-left: calc(50% - 50vw);
  margin-right: calc(50% - 50vw);
}
</style>
